/**
 * upmix-laws.js — window.__UpmixLaws
 *
 * THE single source of truth for the stereo→surround coefficient laws that more
 * than one tool needs. Created 2026-07-23 when /surround-disc was built: it needs
 * the same upmix as /channel-converter, and a COPY would have silently drifted
 * away from the level fixes landed one day earlier (2026-07-16, `6f318a422`).
 *
 * The two laws that a copy would have got wrong — both were REAL, measured bugs
 * reported from a listener's 5.1 rig, so they must never diverge again:
 *
 *   LFE_WRITE_GAIN = 0.3162 (−10 dB). A playback LFE channel is AUTHORED 10 dB
 *   below the mains because every consumer receiver applies the standard +10 dB
 *   LFE gain on output. Writing LFE at unity (what the tool did before) played
 *   the bass ~+11 dB over the original — «зажат, глух и громче всех».
 *   ⛔ Does NOT apply to a DAW stem channel (e.g. the ".1" of the 3.1 target),
 *      which is a bass STEM, not a playback LFE.
 *
 *   Row normalisation Σ|gain| ≤ 1. Unnormalised rows (matrix surrounds
 *   0.707+0.5 = 1.207, residual fronts 0.8+0.4 = 1.2) hard-clipped loud
 *   ANTI-correlated program — measured 55–56 k clamped samples on a ±0.95 test
 *   tone, audible as the "перегруз" on a wide electric guitar. Ratios are kept
 *   (imaging identical), absolute level drops ~1.6 dB.
 *
 * Tap format (shared with channel-converter's renderMatrix):
 *   row = [[srcChannel, gain], ...]   |   {taps:[...], lp:Hz, hp:Hz, dly:ms}
 *   Output order: 5.1 → FL FR FC LFE BL BR ; 7.1 → FL FR FC LFE BL BR SL SR
 */
(function (w) {
    'use strict';
    if (w.__UpmixLaws) return;

    /* −10 dB. Receivers add +10 dB to LFE on playback; authoring at unity is
     * what made the bass shout. 10^(-10/20) = 0.31623; halved per-tap below
     * because both L and R feed the mono LFE sum (0.5 × 0.3162 = 0.158). */
    var LFE_WRITE_GAIN = 0.3162;
    var LFE_TAP = 0.158;          // 0.5 · LFE_WRITE_GAIN, per input channel

    /* Matrix surrounds, normalised from the measured-optimal 0.707/−0.5 ratio
     * (Σ|g| = 1.207 → clipped) to Σ|g| = 1. Ratio preserved: 0.5857/0.4142. */
    var SUR_A = 0.5857, SUR_B = 0.4142;

    var MATRIX = {
        '5.1': {
            simple: [ [[0,1]], [[1,1]], [[0,.5],[1,.5]], [[0,LFE_TAP],[1,LFE_TAP]], [[0,1]], [[1,1]] ],
            matrix: [ [[0,1]], [[1,1]], [[0,.5],[1,.5]], [[0,LFE_TAP],[1,LFE_TAP]],
                      [[0,SUR_A],[1,-SUR_B]], [[0,-SUR_B],[1,SUR_A]] ]
        },
        '7.1': {
            simple: [ [[0,1]], [[1,1]], [[0,.5],[1,.5]], [[0,LFE_TAP],[1,LFE_TAP]], [[0,1]], [[1,1]], [[0,1]], [[1,1]] ],
            matrix: [ [[0,1]], [[1,1]], [[0,.5],[1,.5]], [[0,LFE_TAP],[1,LFE_TAP]],
                      [[0,.6],[1,-.4]], [[0,-.4],[1,.6]],
                      [[0,SUR_A],[1,-SUR_B]], [[0,-SUR_B],[1,SUR_A]] ]
        }
    };

    /* ENHANCED — matrix plus the two things a receiver expects of a real mix:
     * the LFE is band-limited (lowpass 120 Hz) and the surrounds are delayed
     * 15 ms so the front image stays in front (precedence effect).
     * The coefficients already contain the 0.8 surround trim and the 0.5 LFE
     * sum (0.5 · LFE_WRITE_GAIN · 1.2 = 0.1896), so the table — not a filter
     * chain — is the statement of the law. */
    MATRIX['5.1'].enhanced = [
        [[0,1]], [[1,1]], [[0,.5],[1,.5]],
        {taps:[[0,.1896],[1,.1896]], lp:120},
        {taps:[[0,.5656],[1,-.4]], dly:15}, {taps:[[0,-.4],[1,.5656]], dly:15}
    ];
    MATRIX['7.1'].enhanced = [
        [[0,1]], [[1,1]], [[0,.5],[1,.5]],
        {taps:[[0,.1896],[1,.1896]], lp:120},
        {taps:[[0,.3959],[1,-.28]], dly:15}, {taps:[[0,-.28],[1,.3959]], dly:15},
        {taps:[[0,.5656],[1,-.4]], dly:15}, {taps:[[0,-.4],[1,.5656]], dly:15}
    ];

    /* RESIDUAL / Primary-Ambient: the FRONT pair carries what is left after the
     * correlated centre and the decorrelated ambience are extracted, instead of
     * a raw L/R copy. 0.8/−0.4 measured optimal, normalised ÷1.2 to Σ|g| = 1
     * (the unnormalised law hard-clipped anti-correlated program — 55 k clamped
     * samples on a ±0.95 tone). The tail is identical to `enhanced`. */
    var RESID_FL = [[0,0.6667],[1,-0.3333]];
    var RESID_FR = [[1,0.6667],[0,-0.3333]];
    MATRIX['5.1'].residual = [RESID_FL, RESID_FR].concat(MATRIX['5.1'].enhanced.slice(2));
    MATRIX['7.1'].residual = [RESID_FL, RESID_FR].concat(MATRIX['7.1'].enhanced.slice(2));

    /* Height rows for 7.1.4 / 7.1.6 — decorrelated L−R ambience, delayed per row
     * (front 8 ms, middle 15 ms, back 22 ms) so the overheads do not collapse
     * into one phantom. Honest SYNTHETIC height, not object-based Atmos. */
    var HEIGHT_ROWS = [
        {taps:[[0,.42],[1,-.22]], dly:8},    // TFL
        {taps:[[1,.42],[0,-.22]], dly:8},    // TFR
        {taps:[[0,.32],[1,-.26]], dly:22},   // TBL
        {taps:[[1,.32],[0,-.26]], dly:22}    // TBR
    ];
    var HEIGHT_MID = [
        {taps:[[0,.38],[1,-.24]], dly:15},   // TSL
        {taps:[[1,.38],[0,-.24]], dly:15}    // TSR
    ];
    var HEIGHT_ROWS_6 = [HEIGHT_ROWS[0], HEIGHT_ROWS[1], HEIGHT_MID[0], HEIGHT_MID[1], HEIGHT_ROWS[2], HEIGHT_ROWS[3]];
    ['simple','matrix','enhanced','residual'].forEach(function (m) {
        MATRIX['7.1.4'] = MATRIX['7.1.4'] || {};
        MATRIX['7.1.6'] = MATRIX['7.1.6'] || {};
        MATRIX['7.1.4'][m] = MATRIX['7.1'][m].concat(HEIGHT_ROWS);
        MATRIX['7.1.6'][m] = MATRIX['7.1'][m].concat(HEIGHT_ROWS_6);
    });

    /* ── The FFmpeg lane is a PROJECTION of the table above ────────────────
     * Before 2026-09-12 the pan strings were written out by hand next to the
     * table and had to be kept in step by eye. They were not: the hand-written
     * ones addressed inputs BY NAME (`FL=FL|FR=FR`), and a mono file has no FL
     * — FFmpeg then wrote silence into every channel, exit code 0, no warning,
     * for as long as the tool had existed. The Web-Audio renderer never had the
     * bug because it reads taps by INDEX and clamps (`Math.min(i, inCh-1)`).
     *
     * So the emitter below reads the same table and applies the same clamp. A
     * channel NAME never appears on the input side, which is what makes the
     * whole class impossible rather than merely fixed. Verified against the
     * hand-written chains on real music and real speech: bit-identical output
     * for every target × method (see tests/playwright/channel-converter-ab-capture.mjs). */
    var LAYOUT_BY_N = {1:'mono', 2:'stereo', 3:'3.0', 4:'3.1', 6:'5.1', 8:'7.1'};
    function layoutName(n) { return LAYOUT_BY_N[n] || (n + 'c'); }

    function num(x) {
        // Six decimals is below any 24-bit LSB and keeps the strings readable.
        var v = (Math.round(x * 1e6) / 1e6).toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
        return v === '-0' ? '0' : v;
    }

    /* taps → "0.5*c0-0.4142*c1", with the SOURCE CLAMP applied and repeated
     * inputs summed. A mono source turns FL=1·c0 / FR=1·c1 into two rows that
     * both read c0, and a surround row 0.5857·c0 − 0.4142·c0 into 0.1715·c0 —
     * exactly what renderMatrix computes when both taps land on channel 0. */
    function tapExpr(taps, srcCh) {
        var last = Math.max(0, (srcCh || 2) - 1), byIdx = {};
        (taps || []).forEach(function (t) {
            var i = Math.min(t[0], last);
            byIdx[i] = (byIdx[i] || 0) + t[1];
        });
        var terms = Object.keys(byIdx).map(Number).sort(function (a, b) { return a - b; })
            .filter(function (i) { return Math.abs(byIdx[i]) > 1e-9; })
            .map(function (i) { return num(byIdx[i]) + '*c' + i; });
        if (!terms.length) return '0*c0';
        return terms.join('+').replace(/\+-/g, '-');
    }

    function rowOf(r) { return Array.isArray(r) ? {taps:r, lp:0, hp:0, dly:0} : {taps:r.taps, lp:r.lp||0, hp:r.hp||0, dly:r.dly||0}; }

    /* FFmpeg arguments that render `rows` from a source with `srcCh` channels.
     * A table with no per-row filtering is one `pan`; anything with a lowpass or
     * a delay becomes one branch per output channel, merged back in order. */
    /* A row's `lp` / `hp` is a corner frequency and nothing else: the filter's
       shape is FFmpeg's default for `lowpass` / `highpass` — Q 0.707, Butterworth,
       which is what the graph below emits by not naming a width. The Web Audio
       renderers of the same rows have to be told: BiquadFilterNode reads the Q
       of those two filter types in DECIBELS and defaults to 1 dB — a resonance
       of +1 dB at the corner where FFmpeg is 3 dB down. Until 2026-10-07 they
       left it at that, and the subwoofer and the rear band edges of every
       filtered method came out up to 4 dB hotter in the WAV lane, the monitor
       and the disc than in FLAC / OGG / M4A (measured: +4.01 dB at 120 Hz).
       Every renderer takes the number from here. */
    var BIQUAD_Q_DB = 20 * Math.log10(0.707);   // −3.0116

    function ffmpegArgs(rows, srcCh) {
        var chans = (rows || []).map(rowOf), n = chans.length;
        if (!n) return [];
        var plain = chans.every(function (c) { return !c.lp && !c.hp && !c.dly; });
        if (plain) {
            return ['-af', 'pan=' + layoutName(n) + '|' + chans.map(function (c, o) {
                return 'c' + o + '=' + tapExpr(c.taps, srcCh);
            }).join('|')];
        }
        // fltp everywhere: amerge refuses to negotiate across branches otherwise
        // (the same lesson the hand-written chains carried).
        var af = 'aformat=sample_fmts=fltp';
        var labels = chans.map(function (_, i) { return 'b' + i; });
        var graph = '[0:a]' + af + ',asplit=' + n + '[' + labels.join('][') + '];';
        chans.forEach(function (c, i) {
            var branch = '[' + labels[i] + ']pan=mono|c0=' + tapExpr(c.taps, srcCh);
            if (c.lp)  branch += ',lowpass=f=' + c.lp;
            if (c.hp)  branch += ',highpass=f=' + c.hp;
            if (c.dly) branch += ',adelay=' + c.dly + ':all=1';
            graph += branch + ',' + af + ':channel_layouts=mono[o' + i + '];';
        });
        graph += chans.map(function (_, i) { return '[o' + i + ']'; }).join('')
              + 'amerge=inputs=' + n + ',pan=' + layoutName(n) + '|'
              + chans.map(function (_, o) { return 'c' + o + '=c' + o; }).join('|') + '[out]';
        return ['-filter_complex', graph, '-map', '[out]'];
    }

    /* Kept for callers that ask by name. Both now go through ffmpegArgs, so a
     * tool cannot get a different law than the table states. `srcCh` defaults to
     * stereo; pass the real count so a mono source is clamped rather than silent. */
    function panFilter(target, method, srcCh) {
        var rows = (MATRIX[target] || MATRIX['5.1'])[method === 'simple' ? 'simple' : 'matrix'];
        var args = ffmpegArgs(rows, srcCh);
        return args[0] === '-af' ? args[1] : '';
    }

    function upmixArgs(target, method, srcCh) {
        var set = MATRIX[target] || MATRIX['5.1'];
        return ffmpegArgs(set[method] || set.matrix, srcCh);
    }

    /* Worst-case output gain of a row set — a build-time assertion helper so a
     * future edit that re-introduces clipping fails loudly instead of quietly. */
    function worstRowGain(rows) {
        var worst = 0;
        (rows || []).forEach(function (row) {
            var taps = row && row.taps ? row.taps : row;
            var s = 0;
            (taps || []).forEach(function (t) { s += Math.abs(t[1]); });
            if (s > worst) worst = s;
        });
        return worst;
    }

    w.__UpmixLaws = {
        BIQUAD_Q_DB: BIQUAD_Q_DB,
        LFE_WRITE_GAIN: LFE_WRITE_GAIN,
        LFE_TAP: LFE_TAP,
        SUR_A: SUR_A,
        SUR_B: SUR_B,
        MATRIX: MATRIX,
        // The height rows are part of the law too: the spectral engine in
        // /channel-converter builds its own 7.1.4 / 7.1.6 by appending these to
        // its per-bin bed, and a second copy of them would drift the same way
        // the pan strings did.
        HEIGHT_ROWS: HEIGHT_ROWS,
        HEIGHT_ROWS_6: HEIGHT_ROWS_6,
        ffmpegArgs: ffmpegArgs,
        layoutName: layoutName,
        panFilter: panFilter,
        upmixArgs: upmixArgs,
        worstRowGain: worstRowGain
    };
})(window);
